import { JSX } from 'react'

import {
  AddressDisplay,
  List,
  Message,
  TokenBalance,
} from '~/components/component-library'
import { StateChangeErrorMessage } from '~/components/txn-display/state-change/components/StateChangeErrorMessage'
import { TxnData } from '~/components/txn-display/state-change/types'
import { OrgRegularTransaction, PublicRegularTransaction } from '~/types'

const buildExceptionMessage = ({
  hasError,
  hasNoTransfers,
  errorMessage,
  isSimulationRevert,
}: {
  hasError: boolean
  hasNoTransfers: boolean
  errorMessage?: string
  isSimulationRevert?: boolean
}): JSX.Element | undefined => {
  if (hasError) {
    return (
      <StateChangeErrorMessage
        errorMessage={errorMessage}
        isSimulationRevert={isSimulationRevert}
      />
    )
  }

  if (hasNoTransfers) {
    return (
      <Message variant="info" title="No transfers found">
        No asset transfers detected in this transaction
      </Message>
    )
  }

  return undefined
}

export const StateChangeException = ({
  transaction,
  txnData,
  hasNoTransfers,
  hasError,
  errorMessage,
  isSimulationRevert,
  secondaryDisplayButtons,
}: {
  transaction?: OrgRegularTransaction | PublicRegularTransaction
  txnData?: TxnData
  hasNoTransfers: boolean
  hasError: boolean
  errorMessage?: string
  isSimulationRevert?: boolean
  secondaryDisplayButtons?: JSX.Element
}) => {
  const address =
    transaction?.smartAccountAddress ?? txnData?.smartAccountAddress
  const chainId = transaction?.chainId ?? txnData?.chainId
  // @ts-expect-error - transactionMetadata is not typed
  const token = transaction?.transactionMetadata?.[0]?.sellTokenAddress
  // @ts-expect-error - transactionMetadata is not typed
  const tokenAmount = transaction?.transactionMetadata?.[0]?.sellAmount

  const message = buildExceptionMessage({
    hasError,
    hasNoTransfers,
    errorMessage,
    isSimulationRevert,
  })

  // only show account row if there's token metadata to display
  if (address && chainId && token && tokenAmount != null) {
    return (
      <List
        label="State change"
        secondaryDisplay={secondaryDisplayButtons}
        items={[
          {
            key: 'fallback',
            content: (
              <AddressDisplay
                address={address}
                chainId={chainId}
                layout="two-line"
              />
            ),
            meta: (
              <TokenBalance
                address={token}
                chainId={chainId}
                tokenAmount={tokenAmount}
                layout="standard"
                size="base"
                direction="outflow"
                showNetworkIcon={true}
              />
            ),
          },
        ]}
        footer={message}
      />
    )
  }

  return message
}
